import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { Badge } from "@/components/ui";
import { cn, toApiSort } from "@/lib/utils";
import { RootState } from "@/redux/store";
import { routes } from "@/routes/utils";
import { useGetBulkUploadHistory } from "@/services";
import { formatDateTime, uploadStatusMeta } from "./consts";

const useBulkUploadHistory = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const communityData = useSelector((state: RootState) => state.currentData.communityData);
  const communityId = communityData?.communityId?.toString();

  const [globalFilter, setGlobalFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [pageNumber, setPageNumber] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [sorting, setSorting] = useState<any>([{ id: "uploadedAt", desc: true }]);

  // Users grid of the same persona (Admin / CAM), opened on this community
  const usersPath = `${location.pathname.split(`/${routes.uploadHistory}`)[0]}?communityId=${communityId ?? ""}`;

  const { data: historyData, isLoading, refetch } = useGetBulkUploadHistory({
    communityId: communityId as string,
    data: {
      filters: statusFilter === "ALL" ? [] : [{ field: "status", value: statusFilter, operator: "EQUAL" }],
      search: globalFilter,
      searchFields: ["fileName", "uploadedByName", "committedByName"],
      pageNumber,
      pageSize,
      sort: toApiSort(sorting, { field: "uploadedAt", direction: "DESC" }),
    },
    queryParams: { enabled: !!communityId },
  });

  useEffect(() => {
    if (communityId) refetch();
  }, [pageNumber, pageSize, sorting, statusFilter]);

  useEffect(() => {
    if (communityId && (globalFilter?.length >= 2 || globalFilter?.length == 0)) {
      setPageNumber(0);
      refetch();
    }
  }, [globalFilter]);

  const onStatusFilterChange = (value: string) => {
    setPageNumber(0);
    setStatusFilter(value);
  };

  const onRowClick = (row: any) => navigate(`${row.original.attachmentId}`);

  const count = (value: any) => (value ? Number(value) : 0);

  const columns = [
    {
      accessorKey: "uploadedAt",
      header: "Uploaded",
      accessorFn: (row: any) => formatDateTime(row?.uploadedAt),
    },
    {
      accessorKey: "uploadedByName",
      header: "Uploaded By",
      accessorFn: (row: any) => row?.uploadedByName || "-",
    },
    {
      accessorKey: "fileName",
      header: "File",
      accessorFn: (row: any) => row?.fileName || "-",
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ row }: any) => {
        const meta = uploadStatusMeta[row.original?.status];
        return <Badge className={cn("rounded-full", meta?.className)}>{meta?.label ?? row.original?.status}</Badge>;
      },
    },
    {
      accessorKey: "totalRows",
      header: "Rows",
      accessorFn: (row: any) => (row?.status === "COMMITTED" ? String(count(row?.totalRows)) : "-"),
    },
    {
      accessorKey: "newUserCount",
      header: "New",
      accessorFn: (row: any) => (row?.status === "COMMITTED" ? String(count(row?.newUserCount)) : "-"),
    },
    {
      accessorKey: "mergedCount",
      header: "Linked",
      accessorFn: (row: any) => (row?.status === "COMMITTED" ? String(count(row?.mergedCount)) : "-"),
    },
    {
      accessorKey: "skippedCount",
      header: "Skipped",
      accessorFn: (row: any) => (row?.status === "COMMITTED" ? String(count(row?.skippedCount)) : "-"),
    },
    {
      accessorKey: "errorCount",
      header: "Errors",
      cell: ({ row }: any) =>
        row.original?.status === "COMMITTED"
          ? <span className={count(row.original?.errorCount) ? "text-red-600 font-medium" : ""}>{count(row.original?.errorCount)}</span>
          : "-",
    },
    {
      accessorKey: "committedAt",
      header: "Imported",
      accessorFn: (row: any) =>
        row?.committedAt ? `${formatDateTime(row?.committedAt)}${row?.committedByName ? ` by ${row.committedByName}` : ""}` : "-",
    },
  ];

  const breadcrumbData = [
    { link: "", name: "Master Data" },
    { link: "", name: "People" },
    { link: usersPath, name: `Manage Users - ${communityData?.name ?? ""}` },
    { link: "", name: <span className="text-primary">Upload History</span> },
  ];

  return {
    communityData,
    breadcrumbData,
    columns,
    historyData,
    isLoading,
    globalFilter,
    setGlobalFilter,
    statusFilter,
    onStatusFilterChange,
    pageNumber,
    pageSize,
    setPageNumber,
    setPageSize,
    sorting,
    setSorting,
    onRowClick,
  };
};

export default useBulkUploadHistory;
